// i18next-extract-mark-ns-start MyAccount
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import withOrderContext from '../../withOrderContext';
import withCustomerContext from '../../withCustomerContext';
import withNavigation from '../../hoc/withNavigation';
import { navigate } from '../../services/navigation';
import callApi from '../../services/api';

import { colors, tabletThresholdPixels, Button as ButtonV3 } from '../home/v3/styledComponents';
import routesMap from '../../Routes';
import {
  isEmailValid, isPhoneValid, doesEmailLookValid, getNearestFrequentDomain,
  getDistanceFromFrequentNames, getDistanceFromFrequentDomains, getNearestFrequentName, getStringWithoutNumbers,
} from '../../services/checkingFormat';
import { pushToLayer } from '../../services/googleTagManager';
import OutInSideClick from '../home/v2/OutInSideClick';
import CustomerForm from '../order/CustomerForm';
import { OpacityContainer, PopupContainer, StyledCloseIcon } from '../home/v4/styledComponents';
import closeIcon from '../../assets/order/knitTear/close.svg';

const Title = styled.div`
  font-size: 26px;
  font-weight: 500;
  margin-bottom: 26px;
  @media (max-width: ${tabletThresholdPixels}) {
    font-size: 24px;
    margin-bottom: 22px;
  }
`;

const Button = styled(ButtonV3)`
  margin: 0px;
  width: 245px;
  @media (max-width: ${tabletThresholdPixels}) {
    min-width: unset;
    width: 205px;
  }
`;

const SpanLink = styled.span`
  text-decoration: underline;
  color: ${colors.black};
  cursor: pointer;
`;

const Span = styled.span`
  color: ${colors.black};
  line-height: 15px;
  margin-top: -15px;

  @media (max-width: ${tabletThresholdPixels}) {
    width: 258px;
    margin-bottom: 5px;
  }
`;

const Text = styled.p`
  margin-top: 0px;
  margin-bottom: 25px;
  text-align: center;
  width: 100%;
  @media (max-width: ${tabletThresholdPixels}) {
    font-size: 14px;
    width: 275px;
  }
`;

const invalidEmail = 'Adresse non valide';

const getRegisterInputs = () => ({
  firstname: {
    value: '', name: 'firstname', label: 'Prénom', isRequired: true, size: 'large', isMobileLarge: true,
  },
  lastname: {
    value: '', name: 'lastname', label: 'Nom', isRequired: true, size: 'large', isMobileLarge: true,
  },
  email: {
    value: '', name: 'email', label: 'Adresse mail', isRequired: true, size: 'large', isMobileLarge: true,
  },
  errorEmail: { name: 'errorEmail', isActive: false, label: invalidEmail },
  errorEmailValidity: {
    name: 'errorEmailValidity',
    isActive: false,
    label: '',
    withDefaultProps: true,
  },
  phone: {
    name: 'phone', label: 'Téléphone', isRequired: true, size: 'large', isMobileLarge: true,
  },
  errorPhone: { name: 'errorPhone', isActive: false, label: 'Numéro de téléphone non valide' },
});

export const getPasswordInput = () => ({
  password: {
    type: 'number',
    value: '',
    name: 'password',
    label: 'Code de sécurité',
    isRequired: true,
    size: 'medium',
    isMobileLarge: true,
    withSpinner: true,
  },
  errorConnection: { name: 'errorConnection', isActive: false, label: 'Adresse mail/Numéro de téléphone ou/et mot de passe incorrect' },
});

function PopupLogin({
  closePopup, emailOrPhone, isAlreadyCustomer, customerContext: { setCustomer, setCustomerAndGetOrders, setSelectedOrder }, orderContext: { country },
  order, isFromOrderDetails,
}) {
  const { t } = useTranslation();
  const [passwordForm, setPasswordForm] = useState(getPasswordInput());
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [registerForm, setRegisterForm] = useState(getRegisterInputs());

  const setFormInputValue = (target, listName) => {
    const { name, value } = target;
    if (listName === 'passwordForm') {
      const newPasswordForm = { ...passwordForm };
      newPasswordForm[name].value = value;
      setPasswordForm(newPasswordForm);
    } else if (listName === 'registerForm') {
      const newRegisterForm = { ...registerForm };
      newRegisterForm[name].value = value;
      setRegisterForm(newRegisterForm);
    }
  };

  const setEmail = (email) => {
    const newRegisterForm = { ...registerForm };
    newRegisterForm.email.value = email;
    newRegisterForm.errorEmail.isActive = false;
    newRegisterForm.email.error = false;
    newRegisterForm.errorEmailValidity.isActive = false;
    newRegisterForm.errorEmailValidity.label = '';
    setRegisterForm(newRegisterForm);
  };

  const getEmailValidityLabel = () => {
    const { email, firstname, lastname } = registerForm;
    const emailName = email.value.split('@')[0];
    const emailDomain = email.value.split('@')[1];
    const distanceFromFrequentNames = getDistanceFromFrequentNames(emailName, firstname.value, lastname.value);
    const comparedEmailName = getStringWithoutNumbers(emailName);
    const emailNameLabel = [1, 2].includes(distanceFromFrequentNames)
      ? getNearestFrequentName(emailName, firstname.value, lastname.value)
      : comparedEmailName;
    const distanceFromFrequentDomains = getDistanceFromFrequentDomains(emailDomain);
    const emailDomainLabel = [1, 2].includes(distanceFromFrequentDomains) ? getNearestFrequentDomain(emailDomain) : emailDomain;
    const validEmail = `${emailName.replace(comparedEmailName, emailNameLabel)}@${emailDomainLabel}`;

    return (
      <Span>
        {t('Voulez-vous dire ')}
        {' '}
        <SpanLink onClick={() => setEmail(validEmail)}>{validEmail}</SpanLink>
        {' '}
        :) ?
      </Span>
    );
  };

  const updateFormInputValue = (form, name) => {
    if (name === 'email') {
      const isValid = isEmailValid(form.email.value);
      form.errorEmail.isActive = !isValid;
      form.email.error = !isValid;
      if (form.errorEmail.label) form.errorEmail.label = invalidEmail;
      if (form.errorEmailValidity) {
        form.errorEmailValidity.isActive = !doesEmailLookValid(form.email.value, form.firstname.value, form.lastname.value);
        form.errorEmailValidity.label = form.errorEmailValidity.isActive ? getEmailValidityLabel() : '';
      }
    } else if (name === 'phone') {
      const isValid = isPhoneValid(form.phone.value, country);
      form.errorPhone.isActive = !isValid;
      form.phone.error = !isValid;
    } else {
      form[name].error = !form[name].value;
    }
  };

  const checkFormInputValue = (name, listName) => {
    if (listName === 'passwordForm') {
      const newPasswordForm = { ...passwordForm };
      updateFormInputValue(newPasswordForm, name);
      setPasswordForm(newPasswordForm);
    } else if (listName === 'registerForm') {
      const newRegisterForm = { ...registerForm };
      updateFormInputValue(newRegisterForm, name);
      setRegisterForm(newRegisterForm);
    }
  };

  const login = (callApiBody) => {
    callApi('loginWithTemporaryPassword', 'post', callApiBody)
      .then(({ customer }) => {
        pushToLayer({ event: 'Login' });
        setCustomerAndGetOrders(customer);
        closePopup();
        if (![routesMap.Step1.url, routesMap.Step2.url, routesMap.Step3.url].includes(window.location.pathname)) {
          if (isFromOrderDetails) {
            setSelectedOrder({ ...order });
            navigate(routesMap.MyOrderDetails.url);
          } else navigate(routesMap.MyAccount.url);
        }
      })
      .catch(() => {
        passwordForm.errorConnection.isActive = true;
        setPasswordForm({ ...passwordForm });
        setIsLoginLoading(false);
      });
  };

  const isPasswordDone = () => {
    let hasError = false;
    if (passwordForm.password.value === '') {
      hasError = true;
    }
    if (hasError) setPasswordForm({ ...passwordForm });
    return !hasError;
  };

  const loginWithTemporaryPassword = () => {
    if (!isPasswordDone()) return;
    setIsLoginLoading(true);

    const isEmail = isEmailValid(emailOrPhone);

    const callApiBody = {
      email: isEmail ? emailOrPhone.toLowerCase() : null,
      phone: isEmail ? null : emailOrPhone,
      temporaryPassword: passwordForm.password.value,
    };

    login(callApiBody);
  };

  const isRegisterDone = () => {
    let hasError = false;
    Object.values(registerForm)
      .forEach(({ isRequired, value, name }) => {
        registerForm[name].error = false;
        if (isRequired && !value) {
          registerForm[name].error = true;
          hasError = true;
        }
      });
    if (hasError) setRegisterForm({ ...registerForm });
    if (!hasError) {
      hasError = registerForm.email.error !== false || registerForm.phone.error !== false;
    }
    return !hasError;
  };

  const register = () => {
    const {
      firstname, lastname, email, phone,
    } = registerForm;
    if (!isRegisterDone()) return;
    callApi('register', 'post', {
      firstname: firstname.value,
      lastname: lastname.value,
      email: email.value,
      phone: phone.value,
    })
      .then(({ customer }) => {
        pushToLayer({ event: 'Register' });
        setCustomer(customer);
        closePopup();
        if (![routesMap.Step1.url, routesMap.Step2.url, routesMap.Step3.url].includes(window.location.pathname)) {
          navigate(routesMap.MyAccount.url);
        }
      })
      .catch(() => {
        registerForm.errorEmail.label = 'Cet email a déjà un compte associé';
        registerForm.errorEmail.isActive = true;
        registerForm.email.error = true;
        setRegisterForm({ ...registerForm });
      });
  };

  return (
    <OpacityContainer>
      <OutInSideClick handleClickOutside={closePopup}>
        <PopupContainer>
          <StyledCloseIcon src={closeIcon} alt="close" onClick={closePopup} />
          <Title>{isAlreadyCustomer ? t('Connexion') : t('Inscription')}</Title>
          <Text>{t('À votre boîte mail ou SMS !')}</Text>
          {isAlreadyCustomer ? (<Text>{t('Un mail ou SMS vient de vous être envoyé pour valider votre connexion 🙂')}</Text>)
            : (<Text>{t('Pas encore inscrit ? Créer un compte client pour continuer 🙂')}</Text>)}
          <CustomerForm
            inputsList={isAlreadyCustomer ? passwordForm : registerForm}
            listName={isAlreadyCustomer ? 'passwordForm' : 'registerForm'}
            isLoginLoading={isLoginLoading}
            checkFormInputValue={checkFormInputValue}
            setFormInputValue={setFormInputValue}
            compact
            dataCy="popup-"
            isAlreadyCustomer={isAlreadyCustomer}
          />
          {isAlreadyCustomer
              && <Button black onClick={loginWithTemporaryPassword}>{t('Se connecter')}</Button>}
          {!isAlreadyCustomer
              && <Button black onClick={register} data-cy="inscription">{t('S‘inscrire')}</Button>}
        </PopupContainer>
      </OutInSideClick>
    </OpacityContainer>
  );
}

PopupLogin.propTypes = {
  closePopup: PropTypes.func.isRequired,
  customerContext: PropTypes.shape({
    setCustomer: PropTypes.func.isRequired,
    setCustomerAndGetOrders: PropTypes.func.isRequired,
    setSelectedOrder: PropTypes.func.isRequired,
  }).isRequired,
  orderContext: PropTypes.shape({
    country: PropTypes.shape({}).isRequired,
  }).isRequired,
  emailOrPhone: PropTypes.string.isRequired,
  isAlreadyCustomer: PropTypes.bool.isRequired,
  order: PropTypes.shape({}),
  isFromOrderDetails: PropTypes.bool,
};

PopupLogin.defaultProps = {
  order: {},
  isFromOrderDetails: false,
};

export default withNavigation(
  withCustomerContext(
    withOrderContext(PopupLogin),
  ),
);
